import { useCallback, useEffect, useRef, useState } from "react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { MarqueeRow, StageFX, StreakFlame } from "./CasinoFX";
import "./arcade-anim.css";

const Runner = ({ phase }) => {
  const running = phase === "running";
  const fig = phase === "caught" ? "arc-caught" : phase === "win" ? "" : running ? "" : "arc-bob";
  return (
    <g transform="translate(36,108)">
      <g className={phase === "win" ? "arc-sprint" : ""}>
        <g className={`arc-fig ${fig}`}>
          <line className={running || phase === "win" ? "arc-leg-a" : ""} x1="-2" y1="-16" x2="-8" y2="2" stroke="#1b2130" strokeWidth="4.5" strokeLinecap="round" />
          <line className={running || phase === "win" ? "arc-leg-b" : ""} x1="2" y1="-16" x2="9" y2="2" stroke="#1b2130" strokeWidth="4.5" strokeLinecap="round" />
          <rect x="-8" y="-42" width="16" height="28" rx="7" fill="#089981" />
          <line className={running || phase === "win" ? "arc-arm-a" : ""} x1="-4" y1="-38" x2="-13" y2="-24" stroke="#089981" strokeWidth="4" strokeLinecap="round" />
          <line className={running || phase === "win" ? "arc-arm-b" : ""} x1="4" y1="-38" x2="13" y2="-24" stroke="#089981" strokeWidth="4" strokeLinecap="round" />
          <circle cx="0" cy="-51" r="9" fill="#E8B88A" />
          <circle cx="3.5" cy="-53" r="1.4" fill="#0B0E14" />
          <path d="M -8 -56 A 9 9 0 0 1 8 -56" stroke="#F7B731" strokeWidth="4" fill="none" />
        </g>
      </g>
    </g>
  );
};

/* 🚦 Green Light — animated runner. Arm Rise/Fall, tap GO only on green: one
   real 1-tick Rise/Fall contract fires; the runner sprints on a win, gets
   caught on a loss. Red taps = false start, no money moves. */
const GreenLight = ({ runContract, busy }) => {
  const [light, setLight] = useState("red");
  const [dir, setDir] = useState("up");
  const [res, setRes] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | running | win | caught
  const [falseStart, setFalseStart] = useState(false);
  const [tally, setTally] = useState({ w: 0, l: 0, net: 0 });
  const [wstreak, setWstreak] = useState(0);
  const firing = useRef(false);
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const followRef = useRef(false);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);
  const auto = useAutoRun("greenlight");

  useEffect(() => {
    let alive = true;
    let t;
    const cycle = () => {
      if (!alive) return;
      setLight((prev) => (prev === "red" ? "yellow" : prev === "yellow" ? "green" : "red"));
      t = setTimeout(cycle, 900 + Math.random() * 900);
    };
    t = setTimeout(cycle, 1000);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  const go = async () => {
    if (busy || auto.active) return;
    if (light !== "green") {
      setFalseStart(true);
      setTimeout(() => setFalseStart(false), 900);
      return;
    }
    auto.burst(fire);
  };

  const fire = async () => {
    if (firing.current) return null;
    firing.current = true;
    setRes(null); setPhase("running");
    // 🎯 signal-armed runs follow the freshest best signal each play
    const s = followRef.current ? mktRef.current?.signal : null;
    const d = s && !s.wait ? (s.side === "RISE" ? "up" : "down") : dir;
    if (d !== dir) setDir(d);
    const r = await runContract({
      contractType: d === "up" ? "CALL" : "PUT",
      side: d === "up" ? "RISE" : "FALL", label: "Green Light", symbol: symRef.current,
    });
    firing.current = false;
    if (!r) { setPhase("idle"); return null; }
    setRes(r);
    setPhase(r.won ? "win" : "caught");
    setWstreak((s) => (r.won ? s + 1 : 0));
    setTally((t2) => ({ w: t2.w + (r.won ? 1 : 0), l: t2.l + (r.won ? 0 : 1), net: Math.round((t2.net + r.profit) * 100) / 100 }));
    return r;
  };

  return (
    <div data-testid="greenlight-game" className="space-y-4">
      <HowToPlay id="greenlight"
        steps={[
          "The ⚡ Signal Engine scores momentum on all 10 markets (move flow, price vs 20-tick average, streaks) and locks the strongest setup — or says WAIT when it's mixed.",
          "Arm a direction: tap ⚡ Arm the signal to take the detected side, or pick ⬆ Rise / ⬇ Fall yourself.",
          "Watch the traffic light — tap GO only while it's GREEN. Tapping on red/yellow is a false start and spends nothing.",
          "On GO, one real 1-tick Rise/Fall contract fires on the locked market — the runner sprints while it settles.",
        ]}
        win="the next tick closes in your armed direction — a clean run past the finish flag."
        payout="~×1.9 your stake on a clean run; a caught run loses the stake. Each run is ~50/50."
        tip="the light is the fun layer — it never changes the odds. Use the scanner flow + a session budget, not reflexes." />

      <MarketScanner id="greenlight" mode="rise" onChange={onScan}
        note="↑/↓ = share of up vs down moves in the last 50 ticks · recent flow, NOT a prediction" />

      {/* 🏃 the track — stadium night run */}
      <div className="relative rounded-xl border-2 border-[#F7B731]/40 bg-gradient-to-b from-[#10141f] to-[#0B0E14] overflow-hidden"
        style={{ boxShadow: "0 0 20px rgba(247,183,49,.08), inset 0 0 40px rgba(0,0,0,.5)" }}>
        <MarqueeRow pos="top" />
        <StageFX outcome={res && (phase === "win" || phase === "caught") ? (res.won ? "win" : "loss") : null} />
        <svg viewBox="0 0 320 125" className="w-full h-32">
          {/* stadium crowd */}
          <g opacity="0.3">
            {Array.from({ length: 18 }).map((_, i) => (
              <circle key={i} cx={6 + i * 18} cy={16 + (i % 3) * 6} r="4.5" fill="#1b2130" />
            ))}
          </g>
          {/* floodlight cones */}
          <polygon points="60,0 20,118 110,118" fill="#F7B731" className="spotlight" />
          <polygon points="260,0 210,118 305,118" fill="#F7B731" className="spotlight" style={{ animationDelay: "1.4s" }} />
          <rect x="0" y="110" width="320" height="15" fill="#161b29" />
          <line x1="0" y1="110" x2="320" y2="110" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="12 12" opacity="0.3"
            className={phase === "running" ? "arc-track-move" : ""} />
          {/* sprint speed lines */}
          {phase === "running" && (
            <g stroke="#F7B731" strokeWidth="2" opacity="0.7">
              <line className="speed-line" x1="70" y1="70" x2="96" y2="70" />
              <line className="speed-line" x1="80" y1="84" x2="110" y2="84" style={{ animationDelay: ".15s" }} />
              <line className="speed-line" x1="66" y1="97" x2="90" y2="97" style={{ animationDelay: ".3s" }} />
            </g>
          )}
          {/* finish flag */}
          <line x1="268" y1="48" x2="268" y2="110" stroke="#ffffff" strokeWidth="2.5" opacity="0.8" />
          <path d="M268 48 L292 56 L268 64 Z" fill="#F7B731" />
          <ellipse cx="36" cy="112" rx="18" ry="4" fill="#000" opacity="0.4" />
          <Runner phase={phase} />
          {/* 💨 dust kicked up while sprinting */}
          {phase === "running" && (
            <g fill="#9ca3af">
              <circle className="dust-puff" cx="24" cy="106" r="3" />
              <circle className="dust-puff" cx="30" cy="109" r="2.4" style={{ animationDelay: ".2s" }} />
              <circle className="dust-puff" cx="20" cy="109" r="2" style={{ animationDelay: ".4s" }} />
            </g>
          )}
          {/* 🚨 the guard pounces on a caught runner */}
          {phase === "caught" && (
            <g className="guard-pounce" transform="translate(66,108)">
              <g>
                <line x1="-2" y1="-16" x2="-8" y2="2" stroke="#3f1d1d" strokeWidth="4.5" strokeLinecap="round" />
                <line x1="2" y1="-16" x2="9" y2="2" stroke="#3f1d1d" strokeWidth="4.5" strokeLinecap="round" />
                <rect x="-8" y="-42" width="16" height="28" rx="7" fill="#F23645" />
                <line x1="-4" y1="-38" x2="-18" y2="-30" stroke="#F23645" strokeWidth="4" strokeLinecap="round" />
                <line x1="4" y1="-36" x2="14" y2="-26" stroke="#F23645" strokeWidth="4" strokeLinecap="round" />
                <circle cx="0" cy="-51" r="9" fill="#E8B88A" />
                <path d="M -9 -56 A 9 9 0 0 1 9 -56 l0 -3 a 9 9 0 0 0 -18 0 z" fill="#0B0E14" />
                <circle cx="-3.5" cy="-53" r="1.4" fill="#0B0E14" />
                <circle cx="3.5" cy="-53" r="1.4" fill="#0B0E14" />
              </g>
              <text x="14" y="-58" fontSize="11" fontWeight="900" fill="#F23645" fontFamily="monospace">!</text>
            </g>
          )}
          {phase === "win" && (
            <g>
              <text x="250" y="48" fontSize="15" className="arc-float">🎉</text>
              <text x="276" y="42" fontSize="13" className="arc-float-d1">🎉</text>
              <text x="296" y="52" fontSize="14" className="arc-float-d2">✨</text>
            </g>
          )}
        </svg>
        {/* traffic light */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5 p-2 rounded-lg bg-[#131722] border border-white/10">
          {["red", "yellow", "green"].map((c) => (
            <span key={c} className={`w-6 h-6 rounded-full transition ${light === c
              ? c === "red" ? "bg-[#F23645] shadow-[0_0_14px_#F23645]" : c === "yellow" ? "bg-[#F7B731] shadow-[0_0_14px_#F7B731]" : "bg-[#089981] shadow-[0_0_14px_#089981]"
              : "bg-white/10"}`} />
          ))}
        </div>
        <div className="absolute top-2 left-3">
          {falseStart && <p className="text-xs font-black font-mono text-[#F23645] animate-pulse arc-pop">🚨 FALSE START — no trade fired</p>}
          {!falseStart && phase === "running" && <p className="text-[11px] font-mono text-[#F7B731] animate-pulse">running — settling the tick…</p>}
          {!falseStart && res && (phase === "win" || phase === "caught") && (
            <p className={`text-sm font-black font-mono arc-pop ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
              {res.won ? "CLEAN RUN ✅" : "CAUGHT ❌"} {res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}
            </p>
          )}
          {!falseStart && !res && phase === "idle" && <p className="text-[10px] font-mono text-white/50">arm a direction · tap GO on green</p>}
        </div>
        {/* 🔥 clean-run streak */}
        {wstreak >= 2 && <div className="absolute left-2 bottom-2 z-10"><StreakFlame n={wstreak} /></div>}
      </div>

      {mkt?.signal && !mkt.signal.wait ? (
        <button data-testid="greenlight-signal-arm" disabled={busy} onClick={() => { followRef.current = true; setDir(mkt.signal.side === "RISE" ? "up" : "down"); }}
          className={`w-full h-12 rounded-lg font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99] ${(dir === "up" ? "RISE" : "FALL") === mkt.signal.side
            ? "bg-[#F7B731]/20 border border-[#F7B731] text-[#F7B731]" : "bg-[#F7B731] hover:brightness-110 text-[#0B0E14]"}`}>
          ⚡ {(dir === "up" ? "RISE" : "FALL") === mkt.signal.side ? `Signal armed — ${mkt.signal.side} · ${mkt.signal.strength}/100 · tap GO on green · follows the best signal` : `Arm the signal — ${mkt.signal.side} · ${mkt.signal.strength}/100`}
        </button>
      ) : (
        <div data-testid="greenlight-signal-wait" className="w-full h-12 rounded-lg border border-dashed border-white/15 grid place-items-center text-[10px] font-mono text-white/45">
          ⏳ no clear setup right now — the engine re-scans every 12s
        </div>
      )}

      <AutoRunBar id="greenlight" auto={auto} noun="runs" />

      <div className="grid grid-cols-3 gap-2">
        <button data-testid="greenlight-up" disabled={busy || auto.active} onClick={() => { followRef.current = false; setDir("up"); }}
          className={`h-14 rounded-lg border font-black text-xs uppercase transition ${dir === "up" ? "border-[#089981] bg-[#089981]/15 text-[#089981]" : "border-white/15 text-white/70"}`}>
          ⬆ Rise{mkt && mkt.risePct >= 56 ? " 🔥" : ""}<span className="block text-[9px] font-mono font-normal normal-case text-white/45">{mkt ? `flow ↑${mkt.risePct}%` : ""}</span>
        </button>
        <button data-testid="greenlight-go" disabled={busy || auto.active} onClick={go}
          className={`h-14 rounded-lg font-black text-lg uppercase transition active:scale-95 disabled:opacity-40 ${light === "green" ? "bg-[#089981] text-white shadow-[0_0_20px_rgba(8,153,129,.5)]" : "bg-[#131722] border border-white/15 text-white/40"}`}>GO</button>
        <button data-testid="greenlight-down" disabled={busy || auto.active} onClick={() => { followRef.current = false; setDir("down"); }}
          className={`h-14 rounded-lg border font-black text-xs uppercase transition ${dir === "down" ? "border-[#F23645] bg-[#F23645]/15 text-[#F23645]" : "border-white/15 text-white/70"}`}>
          ⬇ Fall{mkt && mkt.risePct <= 44 ? " 🔥" : ""}<span className="block text-[9px] font-mono font-normal normal-case text-white/45">{mkt ? `flow ↓${100 - mkt.risePct}%` : ""}</span>
        </button>
      </div>

      <p data-testid="greenlight-tally" className="text-center text-[11px] font-mono text-white/60">
        Clean <b className="text-[#089981]">{tally.w}</b> · Caught <b className="text-[#F23645]">{tally.l}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
      </p>
      <p className="text-center text-[10px] font-mono text-white/40">GO on green = one real 1-tick Rise/Fall contract (~×1.9) on the locked market · ~50/50 each run · red taps never spend money · losses possible</p>
    </div>
  );
};

export default GreenLight;
